// Filled variants, both of them: the design draws a solid warning triangle
// (not the outlined `WarningAmber`) and a solid circular ✕ badge (not the bare
// `Close` glyph), so an outlined pair here reads as a different icon set.
import Cancel from '@mui/icons-material/Cancel'
import Warning from '@mui/icons-material/Warning'
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { RollupConfigVendor } from '@/types/rollup'
import {
  addVendorButtonSx,
  allVendorsPillSx,
  VENDOR_CHIP_GAP,
  vendorChipSx,
  vendorRequiredPillSx,
} from './rollupWarningStyles'

/** Off-screen but still in the accessibility tree. MUI's `visuallyHidden`
 * lives in `@mui/utils`, which we only have transitively, so it's inlined. */
const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const

type RollupConfigVendorsProps = {
  vendors: RollupConfigVendor[]
  isVendorRequired: boolean
  /** Disables the remove/add controls (a disabled config's body isn't just
   * dimmed — its controls must be un-tabbable too, which opacity/pointer-
   * events alone don't do). */
  isDisabled: boolean
  companyName: string
  /** The card's own title — combined with companyName so this section's
   * controls stay distinguishable from a sibling card's identical-looking
   * ones (e.g. two "All Vendors" cards in different companies). */
  configLabel: string
  excludedVendorNumbers: Set<string>
  onRemoveVendor: (vendorNum: string) => void
  onAddVendorClick: () => void
}

/** The "Applies to vendors" section of a rollup config card — vendor-less
 * (All Vendors), vendor-required (blocked by an older vendor-less config for
 * this company), or one removable chip per targeted vendor. Never rendered
 * for the All Companies config, which can't target vendors. */
export default function RollupConfigVendors({
  vendors,
  isVendorRequired,
  isDisabled,
  companyName,
  configLabel,
  excludedVendorNumbers,
  onRemoveVendor,
  onAddVendorClick,
}: RollupConfigVendorsProps) {
  return (
    <Stack spacing={1}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: 'uppercase', fontWeight: 600 }}
      >
        Applies to vendors
      </Typography>
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        alignItems="center"
        sx={{ flexWrap: 'wrap' }}
      >
        {isVendorRequired ? (
          <Chip
            icon={<Warning />}
            sx={vendorRequiredPillSx}
            label="Vendor required — this company already has an All Vendors config"
          />
        ) : vendors.length === 0 ? (
          // Accented, not neutral — the design treats "All Vendors" as an
          // active statement about the config's scope rather than a plain tag.
          <Chip label="All Vendors" sx={allVendorsPillSx} />
        ) : (
          vendors.map((vendor) => {
            const label = vendor.display_name || vendor.vendor_num
            const isExcluded = excludedVendorNumbers.has(vendor.vendor_num)
            // Carried as real (off-screen) text inside the chip rather than
            // an aria-label: a MUI Chip renders a role-less div, on which an
            // aria-label isn't reliably announced. Hidden text is read as
            // part of the chip's content, so screen-reader users get the
            // signal without the chip needing to be a tab stop — which it
            // must not be, since a disabled card's body is inert.
            const exclusionMessage = `${label} is excluded for ${companyName} — this config won't be applied while the exclusion is active. Manage it in the Vendor Exclusions tab.`
            const chip = (
              <Chip
                key={vendor.id}
                sx={(theme) => vendorChipSx(theme, isExcluded)}
                label={
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={VENDOR_CHIP_GAP}
                  >
                    {isExcluded && (
                      <Warning sx={{ fontSize: 14 }} aria-hidden="true" />
                    )}
                    <Typography variant="body2" component="span">
                      {label}
                    </Typography>
                    {isExcluded && (
                      <Box component="span" sx={visuallyHidden}>
                        {exclusionMessage}
                      </Box>
                    )}
                    {/* Neutral rather than inheriting the chip's colour: on an
                        excluded chip a yellow ✕ would read as part of the
                        warning instead of as the control that removes it. */}
                    <IconButton
                      size="small"
                      disabled={isDisabled}
                      aria-label={`Remove ${label}`}
                      onClick={() => onRemoveVendor(vendor.vendor_num)}
                      sx={{
                        p: 0.25,
                        color: 'text.secondary',
                        '&:hover': { color: 'text.primary' },
                      }}
                    >
                      <Cancel sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Stack>
                }
              />
            )
            return isExcluded ? (
              <Tooltip key={vendor.id} title={exclusionMessage}>
                {chip}
              </Tooltip>
            ) : (
              chip
            )
          })
        )}
        <Button
          variant="outlined"
          disabled={isDisabled}
          onClick={onAddVendorClick}
          aria-label={`Add vendor to ${companyName} — ${configLabel}`}
          sx={addVendorButtonSx}
        >
          + Vendor
        </Button>
      </Stack>
    </Stack>
  )
}
